import { useState, useEffect, useMemo } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  VStack,
  HStack,
  Text,
  Avatar,
  Badge,
  Spinner,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Link,
  Input,
  SimpleGrid,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { glassCard, inputStyle } from '../styles'
import { apiFetch } from '../utils/apiFetch'
import { formatShortDate } from '../utils/date'
import RateModal from './RateModal'

interface UserProfileData {
  _id: string
  email: string
  name?: string
  title?: string
  joinDate?: string
  phone?: string
  dietary?: string
  isAdmin: boolean
  isActive: boolean
}

interface ProfileRating {
  _id: string
  beer: { _id: string; name: string; brewery?: string; url?: string }
  meeting: { _id: string; date: string }
  score: number
  notes?: string
  createdAt: string
}

interface MissedRating {
  meeting: { _id: string; date: string }
  beer: { _id: string; name: string; brewery?: string; url?: string }
}

function monthsInClub(joinDate?: string): number | null {
  if (!joinDate) return null
  const joined = new Date(joinDate)
  const now = new Date()
  return (now.getFullYear() - joined.getFullYear()) * 12 + (now.getMonth() - joined.getMonth())
}

type SortOption = 'score-desc' | 'score-asc' | 'date' | 'az'

const SORT_LABELS: Record<SortOption, string> = {
  'score-desc': 'Highest rated',
  'score-asc': 'Lowest rated',
  'date': 'Most recent',
  'az': 'A–Z',
}

export default function UserProfile() {
  const { id } = useParams<{ id: string }>()
  const toast = useToast()
  const { isOpen: isRateOpen, onOpen: onRateOpen, onClose: onRateClose } = useDisclosure()

  const [currentUser] = useState(() => {
    const data = localStorage.getItem('user')
    return data ? JSON.parse(data) : null
  })
  const isAdmin = currentUser?.isAdmin === true
  const canEditRatings = isAdmin && currentUser?._id !== id

  const [profile, setProfile] = useState<UserProfileData | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const [ratings, setRatings] = useState<ProfileRating[]>([])
  const [ratingsLoading, setRatingsLoading] = useState(false)
  const [ratingsLoaded, setRatingsLoaded] = useState(false)
  const [sort, setSort] = useState<SortOption>('score-desc')
  const [ratingsSearch, setRatingsSearch] = useState('')

  // Admin: missed ratings + rate modal
  const [missedRatings, setMissedRatings] = useState<MissedRating[]>([])
  const [missedLoaded, setMissedLoaded] = useState(false)
  const [showMissed, setShowMissed] = useState(false)
  const [ratingTarget, setRatingTarget] = useState<{
    beerId: string; beerName: string; brewery?: string; beerUrl?: string
    meetingId: string; meetingDate: string; meetingDateDisplay: string
    score?: number; notes?: string
  } | null>(null)
  const [rateSaving, setRateSaving] = useState(false)

  useEffect(() => {
    apiFetch(`/api/users/${id}`)
      .then(res => {
        if (!res.ok) { setNotFound(true); setLoading(false); return }
        return res.json()
      })
      .then(data => {
        if (data) { setProfile(data); setLoading(false) }
      })
      .catch(() => setLoading(false))
  }, [id])

  function loadRatings() {
    if (ratingsLoaded) return
    setRatingsLoading(true)
    apiFetch(`/api/users/${id}/ratings`)
      .then(res => res.json())
      .then((data: ProfileRating[]) => { setRatings(data); setRatingsLoaded(true); setRatingsLoading(false) })
      .catch(() => setRatingsLoading(false))

    if (canEditRatings) {
      apiFetch(`/api/users/${id}/missed-ratings`)
        .then(res => { if (!res.ok) throw new Error(); return res.json() })
        .then((data) => { if (Array.isArray(data)) setMissedRatings(data); setMissedLoaded(true) })
        .catch(() => setMissedLoaded(true))
    }
  }

  function openRateModal(target: typeof ratingTarget) {
    setRatingTarget(target)
    onRateOpen()
  }

  async function handleSaveRating(score: number, notes: string) {
    if (!ratingTarget) return
    setRateSaving(true)
    try {
      const res = await apiFetch(`/api/beers/${ratingTarget.beerId}/rate-for/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meetingId: ratingTarget.meetingId, score, notes: notes || undefined }),
      })
      if (res.ok) {
        const saved = await res.json()
        if (ratingTarget.score !== undefined) {
          setRatings(prev => prev.map(r =>
            (r.beer._id === ratingTarget.beerId && r.meeting._id === ratingTarget.meetingId)
              ? { ...r, score: saved.score, notes: saved.notes }
              : r
          ))
        } else {
          const newRating: ProfileRating = {
            _id: saved._id,
            beer: { _id: ratingTarget.beerId, name: ratingTarget.beerName, brewery: ratingTarget.brewery, url: ratingTarget.beerUrl },
            meeting: { _id: ratingTarget.meetingId, date: ratingTarget.meetingDate },
            score: saved.score,
            notes: saved.notes,
            createdAt: saved.createdAt,
          }
          setRatings(prev => [newRating, ...prev])
          setMissedRatings(prev => prev.filter(item =>
            !(item.beer._id === ratingTarget.beerId && item.meeting._id === ratingTarget.meetingId)
          ))
        }
        onRateClose()
        toast({ status: 'success', description: 'Rating saved', duration: 2000, isClosable: true })
      } else {
        const errorBody = await res.json().catch(() => ({}))
        toast({ status: 'error', title: `Save failed (${res.status})`, description: (errorBody as { error?: string }).error ?? 'Unknown error', duration: 5000, isClosable: true })
      }
    } catch (err) {
      toast({ status: 'error', title: 'Network error', description: String(err), duration: 5000, isClosable: true })
    } finally {
      setRateSaving(false)
    }
  }

  async function handleClearRating() {
    if (!ratingTarget) return
    setRateSaving(true)
    try {
      const res = await apiFetch(`/api/beers/${ratingTarget.beerId}/rate-for/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meetingId: ratingTarget.meetingId }),
      })
      if (res.ok) {
        const cleared = ratings.find(r =>
          r.beer._id === ratingTarget.beerId && r.meeting._id === ratingTarget.meetingId
        )
        setRatings(prev => prev.filter(r =>
          !(r.beer._id === ratingTarget.beerId && r.meeting._id === ratingTarget.meetingId)
        ))
        if (cleared) {
          setMissedRatings(prev => [...prev, {
            meeting: cleared.meeting,
            beer: cleared.beer,
          }])
        }
        onRateClose()
        toast({ status: 'success', description: 'Rating cleared', duration: 2000, isClosable: true })
      } else {
        const errorBody = await res.json().catch(() => ({}))
        toast({ status: 'error', title: `Delete failed (${res.status})`, description: (errorBody as { error?: string }).error ?? 'Unknown error', duration: 5000, isClosable: true })
      }
    } catch (err) {
      toast({ status: 'error', title: 'Network error', description: String(err), duration: 5000, isClosable: true })
    } finally {
      setRateSaving(false)
    }
  }

  const sortedRatings = useMemo(() => {
    const query = ratingsSearch.toLowerCase()
    return ratings
      .filter(r => !query || r.beer.name.toLowerCase().includes(query) || r.beer.brewery?.toLowerCase().includes(query))
      .sort((a, b) => {
        if (sort === 'score-desc') return b.score - a.score
        if (sort === 'score-asc') return a.score - b.score
        if (sort === 'date') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        return a.beer.name.localeCompare(b.beer.name)
      })
  }, [ratings, sort, ratingsSearch])

  const filteredMissed = useMemo(() => {
    const query = ratingsSearch.toLowerCase()
    if (!query) return missedRatings
    return missedRatings.filter(m => m.beer.name.toLowerCase().includes(query) || m.beer.brewery?.toLowerCase().includes(query))
  }, [missedRatings, ratingsSearch])

  const avgRating = ratings.length > 0
    ? Math.round((ratings.reduce((sum, r) => sum + r.score, 0) / ratings.length) * 100) / 100
    : null

  const months = monthsInClub(profile?.joinDate)
  const displayTitle = profile?.title || 'Beer Enthusiast'

  return (
    <Box minH="100vh" position="relative" p={{ base: 4, md: 8 }} pt={{ base: 16, md: 20 }}>
      <Box maxW="500px" mx="auto">
        {loading ? (
          <Spinner color="white" />
        ) : notFound ? (
          <Text color="whiteAlpha.600">Member not found.</Text>
        ) : profile && (
          <VStack spacing={6} align="stretch">
            {/* Profile header — always visible */}
            <Box {...glassCard} borderRadius="2xl" p={{ base: 5, md: 8 }} textAlign="center">
              <VStack spacing={4}>
                <Avatar size="xl" name={profile.name ?? profile.email} border="4px solid" borderColor="whiteAlpha.300" />
                <VStack spacing={1}>
                  <Text fontSize="2xl" fontWeight="bold" color="white">
                    {profile.name ?? profile.email}
                  </Text>
                  {profile.name && (
                    <Text color="whiteAlpha.500" fontSize="sm">{profile.email}</Text>
                  )}
                  <Badge colorScheme="yellow" fontSize="sm" px={3} py={1}>
                    {displayTitle}{months !== null ? ` • ${months} months in club` : ''}
                  </Badge>
                  {profile.isAdmin && <Badge colorScheme="orange" fontSize="xs">Admin</Badge>}
                  {!profile.isActive && <Badge colorScheme="red" fontSize="xs">Inactive</Badge>}
                </VStack>
              </VStack>
            </Box>

            <Tabs colorScheme="orange" variant="soft-rounded" onChange={i => { if (i === 1) loadRatings() }}>
              <TabList>
                <Tab color="whiteAlpha.700" _selected={{ color: 'white', bg: 'orange.500' }}>Info</Tab>
                <Tab color="whiteAlpha.700" _selected={{ color: 'white', bg: 'orange.500' }}>Ratings</Tab>
              </TabList>

              <TabPanels>
                {/* Info tab */}
                <TabPanel px={0} pt={4}>
                  {(profile.phone || profile.dietary) ? (
                    <Box {...glassCard} borderRadius="xl" p={{ base: 4, md: 6 }}>
                      <VStack spacing={3} align="stretch">
                        <Text fontWeight="semibold" color="white" fontSize="md">Contact & Info</Text>
                        {profile.phone && (
                          <VStack align="start" spacing={0}>
                            <Text color="whiteAlpha.600" fontSize="xs" textTransform="uppercase" letterSpacing="wide">Phone</Text>
                            <Text color="white" fontSize="sm">{profile.phone}</Text>
                          </VStack>
                        )}
                        {profile.dietary && (
                          <VStack align="start" spacing={0}>
                            <Text color="whiteAlpha.600" fontSize="xs" textTransform="uppercase" letterSpacing="wide">Allergies / Dietary</Text>
                            <Text color="white" fontSize="sm">{profile.dietary}</Text>
                          </VStack>
                        )}
                      </VStack>
                    </Box>
                  ) : (
                    <Text color="whiteAlpha.400" fontSize="sm" fontStyle="italic">No additional info.</Text>
                  )}
                </TabPanel>

                {/* Ratings tab */}
                <TabPanel px={0} pt={4}>
                  {ratingsLoading ? (
                    <Spinner color="white" />
                  ) : !ratingsLoaded ? null : ratings.length === 0 && !canEditRatings ? (
                    <Text color="whiteAlpha.400" fontSize="sm" fontStyle="italic">No ratings yet.</Text>
                  ) : (
                    <VStack spacing={3} align="stretch">
                      {ratings.length > 0 && (
                        <SimpleGrid columns={2} spacing={3}>
                          <Box {...glassCard} p={4} borderRadius="lg" textAlign="center">
                            <Text fontSize="xl" fontWeight="bold" color="white">{ratings.length}</Text>
                            <Text fontSize="xs" color="whiteAlpha.600">Ratings given</Text>
                          </Box>
                          <Box {...glassCard} p={4} borderRadius="lg" textAlign="center">
                            <Text fontSize="xl" fontWeight="bold" color="white">★ {avgRating?.toFixed(2).replace(/\.?0+$/, '')}</Text>
                            <Text fontSize="xs" color="whiteAlpha.600">Avg rating</Text>
                          </Box>
                        </SimpleGrid>
                      )}

                      {/* Search */}
                      <Input
                        placeholder="Search beers or breweries…"
                        value={ratingsSearch}
                        onChange={e => setRatingsSearch(e.target.value)}
                        {...inputStyle}
                      />

                      {/* Admin: toggle between rated / unrated */}
                      {canEditRatings && (
                        <HStack spacing={2}>
                          <Badge cursor="pointer" colorScheme={!showMissed ? 'orange' : 'gray'}
                            onClick={() => setShowMissed(false)} px={2} py={1} borderRadius="md">
                            Ratings
                          </Badge>
                          <Badge cursor="pointer" colorScheme={showMissed ? 'orange' : 'gray'}
                            onClick={() => setShowMissed(true)} px={2} py={1} borderRadius="md">
                            Unrated {missedLoaded ? `(${missedRatings.length})` : '(…)'}
                          </Badge>
                        </HStack>
                      )}

                      {showMissed && canEditRatings ? (
                        /* Unrated view (admin only) */
                        !missedLoaded ? (
                          <Spinner color="white" size="sm" />
                        ) : filteredMissed.length === 0 ? (
                          <Text color="whiteAlpha.500" fontSize="sm" fontStyle="italic">
                            {ratingsSearch ? 'No unrated beers match your search.' : 'No unrated beers from attended meetings.'}
                          </Text>
                        ) : (
                          <VStack spacing={2} align="stretch">
                            {filteredMissed.map((item, idx) => (
                              <Box key={`${item.meeting._id}-${item.beer._id}-${idx}`} {...glassCard} borderRadius="lg" p={3}
                                cursor="pointer"
                                onClick={() => openRateModal({
                                  beerId: item.beer._id,
                                  beerName: item.beer.name,
                                  brewery: item.beer.brewery,
                                  beerUrl: item.beer.url,
                                  meetingId: item.meeting._id,
                                  meetingDate: item.meeting.date,
                                  meetingDateDisplay: formatShortDate(item.meeting.date),
                                })}
                                _hover={{ borderColor: 'orange.400', borderWidth: '1px' }}
                                transition="border-color 0.15s"
                              >
                                <HStack justify="space-between" align="start" flexWrap="wrap" gap={2}>
                                  <VStack align="start" spacing={0} flex={1} minW="120px">
                                    {item.beer.url ? (
                                      <Link href={item.beer.url} isExternal color="white" fontWeight="semibold"
                                        fontSize="sm" textDecoration="underline" _hover={{ color: 'orange.300', textDecoration: 'none' }}
                                        onClick={e => e.stopPropagation()}>
                                        {item.beer.name}
                                      </Link>
                                    ) : (
                                      <Text color="white" fontWeight="semibold" fontSize="sm">{item.beer.name}</Text>
                                    )}
                                    {item.beer.brewery && (
                                      <Text color="whiteAlpha.500" fontSize="xs">{item.beer.brewery}</Text>
                                    )}
                                  </VStack>
                                  <HStack spacing={2} flexShrink={0}>
                                    <Badge colorScheme="orange" fontSize="xs">Rate</Badge>
                                    <Text color="whiteAlpha.400" fontSize="xs">
                                      {formatShortDate(item.meeting.date)}
                                    </Text>
                                  </HStack>
                                </HStack>
                              </Box>
                            ))}
                          </VStack>
                        )
                      ) : (
                        /* Rated view */
                        <VStack spacing={3} align="stretch">
                          <HStack spacing={2} flexWrap="wrap">
                            {(Object.keys(SORT_LABELS) as SortOption[]).map(opt => (
                              <Badge
                                key={opt}
                                cursor="pointer"
                                colorScheme={sort === opt ? 'orange' : 'gray'}
                                onClick={() => setSort(opt)}
                                px={2} py={1} borderRadius="md"
                              >
                                {SORT_LABELS[opt]}
                              </Badge>
                            ))}
                          </HStack>

                          <VStack spacing={2} align="stretch">
                            {sortedRatings.map(rating => (
                              <Box key={rating._id} {...glassCard} borderRadius="lg" p={3}
                                cursor={canEditRatings ? 'pointer' : undefined}
                                onClick={canEditRatings ? () => openRateModal({
                                  beerId: rating.beer._id,
                                  beerName: rating.beer.name,
                                  brewery: rating.beer.brewery,
                                  beerUrl: rating.beer.url,
                                  meetingId: rating.meeting._id,
                                  meetingDate: rating.meeting.date,
                                  meetingDateDisplay: formatShortDate(rating.meeting.date),
                                  score: rating.score,
                                  notes: rating.notes,
                                }) : undefined}
                                _hover={canEditRatings ? { borderColor: 'orange.400', borderWidth: '1px' } : undefined}
                                transition="border-color 0.15s"
                              >
                                <HStack justify="space-between" align="start" flexWrap="wrap" gap={2}>
                                  <VStack align="start" spacing={0} flex={1} minW="120px">
                                    {rating.beer.url ? (
                                      <Link href={rating.beer.url} isExternal color="white" fontWeight="semibold"
                                        fontSize="sm" textDecoration="underline" _hover={{ color: 'orange.300', textDecoration: 'none' }}
                                        onClick={e => e.stopPropagation()}>
                                        {rating.beer.name}
                                      </Link>
                                    ) : (
                                      <Text color="white" fontWeight="semibold" fontSize="sm">{rating.beer.name}</Text>
                                    )}
                                    {rating.beer.brewery && (
                                      <Text color="whiteAlpha.500" fontSize="xs">{rating.beer.brewery}</Text>
                                    )}
                                  </VStack>
                                  <HStack spacing={2} align="center" flexShrink={0}>
                                    <Badge colorScheme="yellow" fontSize="sm" px={2}>★ {rating.score}</Badge>
                                    <Link as={RouterLink} to={`/meetings?m=${rating.meeting._id}`} onClick={e => e.stopPropagation()}>
                                      <Text color="whiteAlpha.400" fontSize="xs" _hover={{ color: 'whiteAlpha.700' }}>
                                        {formatShortDate(rating.meeting.date)}
                                      </Text>
                                    </Link>
                                  </HStack>
                                </HStack>
                                {rating.notes && (
                                  <Text color="whiteAlpha.500" fontSize="xs" mt={1} fontStyle="italic">"{rating.notes}"</Text>
                                )}
                              </Box>
                            ))}
                            {ratings.length === 0 && (
                              <Text color="whiteAlpha.400" fontSize="sm" fontStyle="italic">No ratings yet.</Text>
                            )}
                          </VStack>
                        </VStack>
                      )}
                    </VStack>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </VStack>
        )}
      </Box>

      {/* Admin rate modal */}
      {canEditRatings && (
        <RateModal
          isOpen={isRateOpen}
          onClose={onRateClose}
          beerName={ratingTarget?.beerName ?? ''}
          brewery={ratingTarget?.brewery}
          meetingDateDisplay={ratingTarget?.meetingDateDisplay ?? ''}
          initialScore={ratingTarget?.score}
          initialNotes={ratingTarget?.notes}
          saving={rateSaving}
          onSave={handleSaveRating}
          onClear={handleClearRating}
        />
      )}
    </Box>
  )
}
